import type { Measurement } from '../../api/types'
import { formatNumber, formatPercent } from '../../lib/format'
import type { Tone } from '../../components/ui'

export interface MeasurementBadge {
  key: string
  label: string
  tone: Tone
  icon: 'power' | 'info' | 'triangle-alert' | 'gauge'
}

export function measurementBadges(m: Pick<Measurement, 'operating_state' | 'not_measured' | 'needs_remeasure' | 'mismatches'>): MeasurementBadge[] {
  const out: MeasurementBadge[] = []
  if (m.operating_state === 'off') out.push({ key: 'off', label: 'Apagado', tone: 'off', icon: 'power' })
  if (m.not_measured) out.push({ key: 'not_measured', label: 'Sin medir', tone: 'neutral', icon: 'info' })
  if (m.needs_remeasure) out.push({ key: 'remeasure', label: 'Requiere nueva medición', tone: 'danger', icon: 'triangle-alert' })
  if (m.mismatches.length > 0) out.push({ key: 'mismatch', label: 'Reportado ≠ calculado', tone: 'warn', icon: 'gauge' })
  return out
}

export function utilizationText(m: Pick<Measurement, 'operating_state' | 'not_measured' | 'needs_remeasure' | 'utilization_pct'>): string {
  if (m.operating_state === 'off') return 'Apagado'
  if (m.not_measured || m.utilization_pct === null) return 'Sin medir'
  const text = formatPercent(m.utilization_pct)
  return m.needs_remeasure ? `${text} (sin verificar)` : text
}

const FIELDS: { key: 'effective_power_hp' | 'effective_torque_nm' | 'effective_service_factor' | 'utilization_pct'; label: string }[] = [
  { key: 'effective_power_hp', label: 'Potencia efectiva (Hp)' },
  { key: 'effective_torque_nm', label: 'Torque efectivo (Nm)' },
  { key: 'effective_service_factor', label: 'Factor de servicio efectivo' },
  { key: 'utilization_pct', label: 'Utilización (%)' },
]

type Values = Partial<Record<(typeof FIELDS)[number]['key'], string | null>>

export function loadComparison(m: Pick<Measurement, 'reported' | 'computed' | 'mismatches'>) {
  const reported = m.reported as Values
  const computed = m.computed as Values
  const show = (v: string | null | undefined) => (v === null || v === undefined ? '—' : String(v))
  return FIELDS.map((f) => ({
    key: f.key,
    label: f.label,
    reported: show(reported[f.key]),
    computed: show(computed[f.key]),
    mismatch: m.mismatches.includes(f.key),
  }))
}

export { formatNumber }
